import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { cn } from "../../lib/utils";
import { useLang } from "../../lib/i18n";

const PH = { en: "dd/mm/yyyy", it: "gg/mm/aaaa", es: "dd/mm/aaaa", fr: "jj/mm/aaaa", de: "TT/MM/JJJJ" };

const pad = (n) => String(n).padStart(2, "0");
export const isoToDmy = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const dmyToIso = (s) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return d.getDate() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 ? `${m[3]}-${m[2]}-${m[1]}` : null;
};
const mask = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join("/");
};
const toDate = (iso) => (iso ? new Date(iso.slice(0, 10) + "T12:00:00") : undefined);

export const DateInput = ({ value, onChange, min, max, className, inputClassName, required, ...rest }) => {
  const { lang } = useLang();
  const [text, setText] = useState(isoToDmy(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { if ((dmyToIso(text) || "") !== (value || "").slice(0, 10)) setText(isoToDmy(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const blur = () => { if (text && !dmyToIso(text)) setText(isoToDmy(value)); };
  const type = (e) => {
    const t = mask(e.target.value);
    setText(t);
    if (!t) onChange("");
    else if (dmyToIso(t)) onChange(dmyToIso(t));
  };
  const pick = (d) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setText(isoToDmy(iso));
    onChange(iso);
    setOpen(false);
  };
  const disabled = [min && { before: toDate(min) }, max && { after: toDate(max) }].filter(Boolean);
  return (
    <div className={cn("relative flex items-center", className)}>
      <input inputMode="numeric" placeholder={PH[lang] || PH.en} value={text} onChange={type} onBlur={blur} required={required}
        aria-invalid={!!text && !dmyToIso(text)}
        className={cn("h-10 w-full rounded-md border border-input bg-background px-3 pr-10 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#00A8B5]/40 aria-[invalid=true]:border-rose-400", inputClassName)} {...rest} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="absolute right-2 text-slate-400 hover:text-[#00A8B5]" aria-label="Open calendar" data-testid={rest["data-testid"] ? `${rest["data-testid"]}-calendar` : undefined}><CalendarDays size={16} /></button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="single" weekStartsOn={1} selected={toDate(value)} defaultMonth={toDate(value) || toDate(min)} onSelect={pick} disabled={disabled} />
        </PopoverContent>
      </Popover>
    </div>
  );
};
